import React, { createContext, useContext, useRef, useCallback } from 'react';

// Lets a page with an unsaved local draft (e.g. AdminPortal's Feature Flags tab) intercept
// Sidebar navigation instead of silently losing it. The app's router is a plain BrowserRouter
// (see App.js), not a data router, so react-router's useBlocker (which requires one) isn't
// available here — this is the link-interception alternative: Sidebar checks in with the
// registered guard before letting a <Link> navigate away.
const NavigationGuardContext = createContext({
  registerGuard: () => () => {},
  requestNavigation: () => true,
});

export function NavigationGuardProvider({ children }) {
  const guardRef = useRef(null); // (path) => void — set only while something is dirty

  const registerGuard = useCallback((fn) => {
    guardRef.current = fn;
    return () => { guardRef.current = null; };
  }, []);

  // Returns true if the caller should proceed with navigation immediately.
  // Returns false if a guard intercepted — the guard decides what happens next
  // (e.g. show a confirm dialog, then navigate itself once resolved).
  const requestNavigation = useCallback((path) => {
    if (guardRef.current) { guardRef.current(path); return false; }
    return true;
  }, []);

  return (
    <NavigationGuardContext.Provider value={{ registerGuard, requestNavigation }}>
      {children}
    </NavigationGuardContext.Provider>
  );
}

export const useNavigationGuard = () => useContext(NavigationGuardContext);
